import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CopyToClipboardIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TreeItem, TreeView } from '@mui/lab';
import {
    Box,
    Button,
    ButtonProps,
    createSvgIcon,
    Divider,
    FormControlLabel,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {
    DataGridPro,
    GridCallbackDetails,
    GridCellParams,
    GridToolbarContainer,
    MuiEvent,
    useGridApiContext,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license-pro';
import { useOktaAuth } from '@okta/okta-react';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/snippets/json';
import 'ace-builds/src-noconflict/theme-textmate';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import Backend, {
    Report,
    ReportColumn,
    sObject,
    sObjectField,
    sObjectFieldPicklistValue,
    sObjectRecordTypeInfo,
} from '../backend/Backend';
import './SFExplorer.scss';

interface SFExplorerProps {
    config: any;
    progress: Function;
    hidden: boolean;
}

LicenseInfo.setLicenseKey(
    '4cecb7af5dbb0593d67531c63486e8dbTz04MDM3MyxFPTE3MzM5MTYxMDQwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=',
);

const CustomGridToolbar = (fileName: string, isEnabled: boolean, rows: any[]) => {
    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );

    const buttonBaseProps: ButtonProps = {
        color: 'primary',
        size: 'small',
        startIcon: <ExportIcon />,
    };

    const apiRef = useGridApiContext();

    const handleExport = () =>
        apiRef.current.exportDataAsCsv({
            fileName: fileName || 'output',
            // export all datagrid rows
            getRowsToExport: () => rows.map((row) => row.rowid),
        });

    return (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Button {...buttonBaseProps} onClick={() => handleExport()} disabled={!isEnabled}>
                Download .csv
            </Button>
        </GridToolbarContainer>
    );
};

const SFExplorer = (props: SFExplorerProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [objectList, setObjectList] = useState<Array<sObject> | null>(null);
    const [filteredObjectList, setFilteredObjectList] = useState<Array<sObject> | null>(null);
    const [fieldList, setFieldList] = useState<Array<sObjectField> | null>(null);
    const [recordTypeList, setRecordTypeList] = useState<Array<sObjectRecordTypeInfo> | null>(null);
    const [filteredFieldList, setFilteredFieldList] = useState<Array<sObjectField> | null>(null);
    const [fieldFilterText, setFieldFilterText] = useState<string | null>(null);

    const [reportList, setReportList] = useState<Array<Report> | null>(null);
    const [filteredReportList, setFilteredReportList] = useState<Array<Report> | null>(null);
    const [columnList, setColumnList] = useState<Array<ReportColumn> | null>(null);
    const [filteredColumnList, setFilteredColumnList] = useState<Array<ReportColumn> | null>(null);
    const [columnFilterText, setColumnFilterText] = useState<string | null>(null);

    const [accessToken, setAccessToken] = useState<string>(
        props.config.environments[0].access_token ? props.config.environments[0].access_token : null,
    );
    const [selectedObject, setSelectedObject] = useState<sObject>(null);
    const [selectedObjectField, setSelectedObjectField] = useState<sObjectField>(null);
    const [allDataTypes, setAllDataTypes] = useState<Array<string>>([]);
    const [selectedDataType, setSelectedDataType] = useState<string>(t('sf-explorer.all'));
    const [selectedReport, setSelectedReport] = useState<Report>(null);
    const [selectedReportColumn, setSelectedReportColumn] = useState<ReportColumn>(null);
    const [selectedReportFilters, setSelectedReportFilters] = useState<Array<any>>([]);

    const [soqlQuery, setSoqlQuery] = useState<string | null>(null);
    const [serverSearch, doServerSearch] = useState<boolean>(true);

    let objectListFilterTimeout = null;
    let objectSelectionChangedTimeout = null;
    let fieldListFilterTimeout = null;
    let fieldSelectionChangedTimeout = null;

    let reportListFilterTimeout = null;
    let reportSelectionChangedTimeout = null;
    let columnListFilterTimeout = null;
    let columnSelectionChangedTimeout = null;

    const handleTabChange = (event: React.SyntheticEvent, newIndex: number) => {
        setTabIndex(newIndex);
    };

    useEffect(() => {
        if (props.hidden) {
            return;
        }

        if (tabIndex === 0 && !objectList) {
            fetchSFObjects();
        }

        if (tabIndex === 1 && !reportList) {
            fetchSFReports(null);
        }
    });

    const fetchSFReports = async (searchText: string) => {
        props.progress(true);
        const { resource_url } = props.config.environments[0];
        const reportsDataResponse = await Backend.listSFReports(
            props.config.rowid,
            searchText,
            accessToken,
            oktaAuth,
            resource_url,
        );
        const reportData = reportsDataResponse.data;

        // store access toke for future use
        setAccessToken(reportData.access_token);
        const { reports } = reportData;

        console.log(reports);

        const newReportList = reports.map((o, i) => ({ name: o.name, id: o.id }));
        setReportList(newReportList);

        props.progress(false);

        return newReportList;
    };

    const fetchSFObjects = async () => {
        props.progress(true);
        const { resource_url } = props.config.environments[0];
        const objectsDataResponse = await Backend.listSFObjects(
            props.config.rowid,
            accessToken,
            oktaAuth,
            resource_url,
        );
        const objectData = objectsDataResponse.data;

        // store access toke for future use
        setAccessToken(objectData.access_token);
        const { objects } = objectData;

        const newObjList = objects.map((o, i) => ({ api_name: o.api_name, label: o.label }));
        setObjectList(newObjList);

        props.progress(false);
    };

    const handleObjectFilterChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (objectListFilterTimeout) {
            clearTimeout(objectListFilterTimeout);
        }

        objectListFilterTimeout = setTimeout(() => {
            doObjectFilterChange(e.target.value);
        }, 200);
    };

    function doObjectFilterChange(filterText: string) {
        let newFilteredObjectList = [];

        if (filterText && filterText.trim().length > 2) {
            newFilteredObjectList = objectList.filter(
                (item) =>
                    item.label.toLowerCase().search(filterText.toLowerCase()) !== -1 ||
                    item.api_name.toLowerCase().search(filterText.toLowerCase()) !== -1,
            );
        }

        setFilteredObjectList(newFilteredObjectList);
    }

    const handleSelectedObjectChanged = async (item: sObject) => {
        // if nothing changed, do nothing
        if (selectedObject && selectedObject.api_name === item.api_name) {
            return;
        }

        if (objectSelectionChangedTimeout) {
            clearTimeout(objectSelectionChangedTimeout);
        }

        objectSelectionChangedTimeout = setTimeout(() => {
            doObjectSelectionChange(item);
        }, 200);

        async function doObjectSelectionChange(item: sObject) {
            props.progress(true);
            setSelectedObject(item);
            const { resource_url } = props.config.environments[0];
            const objectDetails = await Backend.getSFObjectDetails(
                props.config.rowid,
                accessToken,
                item.api_name,
                oktaAuth,
                resource_url,
            );
            setRecordTypeList(objectDetails.recordTypeInfos);
            const { fields } = objectDetails;
            const dataTypes = new Set<string>();
            dataTypes.add(t('sf-explorer.all'));

            const newFields = fields.map((item: any, index: any) => {
                dataTypes.add(item.type);
                return {
                    label: item.label,
                    api_name: item.name,
                    type: item.type,
                    referenceTo: item.referenceTo,
                    relationshipName: item.relationshipName,
                    picklistValues: item.picklistValues,
                };
            });

            setAllDataTypes(Array.from(dataTypes.values()));
            setFieldList(newFields);
            doFieldFilterChange(newFields, fieldFilterText, selectedDataType);

            props.progress(false);
        }
    };

    const handleSelectedFieldChanged = async (item: sObjectField) => {
        // if nothing changed, do nothing
        if (selectedObjectField && selectedObjectField.api_name === item.api_name) {
            return;
        }

        if (fieldSelectionChangedTimeout) {
            clearTimeout(fieldSelectionChangedTimeout);
        }

        fieldSelectionChangedTimeout = setTimeout(() => {
            doFieldSelectionChange(item);
        }, 200);

        async function doFieldSelectionChange(item: sObjectField) {
            setSelectedObjectField(item);
        }
    };

    const handleFieldFilterChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (fieldListFilterTimeout) {
            clearTimeout(fieldListFilterTimeout);
        }

        const newFieldFilterText = e.target.value;

        fieldListFilterTimeout = setTimeout(() => {
            setFieldFilterText(newFieldFilterText);
            doFieldFilterChange(fieldList, newFieldFilterText, selectedDataType);
        }, 300);
    };

    function doFieldFilterChange(fieldList: Array<sObjectField>, filterText: string, dt: string) {
        let newFilteredFieldList = [];

        newFilteredFieldList = fieldList.filter((item) => {
            let verdict =
                !filterText ||
                filterText.trim().length === 0 ||
                item.label.toLowerCase().search(filterText.toLowerCase()) !== -1 ||
                item.api_name.toLowerCase().search(filterText.toLowerCase()) !== -1;

            if (dt && dt !== t('sf-explorer.all') && item.type !== dt) {
                verdict = false;
            }

            return verdict;
        });

        setFilteredFieldList(newFilteredFieldList);
    }

    const handleDataTypeChange = (event: SelectChangeEvent) => {
        const dt = event.target.value as string;
        setSelectedDataType(dt);
        doFieldFilterChange(fieldList, fieldFilterText, dt);
    };

    const handleReportFilterChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (reportListFilterTimeout) {
            clearTimeout(reportListFilterTimeout);
        }

        reportListFilterTimeout = setTimeout(() => {
            doReportFilterChange(e.target.value);
        }, 200);
    };

    async function doReportFilterChange(filterText: string) {
        let newFilteredReportList = [];

        if (filterText && filterText.trim().length > 2) {
            if (serverSearch) {
                newFilteredReportList = await fetchSFReports(filterText);
            } else {
                newFilteredReportList = reportList.filter(
                    (item) => item.name.toLowerCase().search(filterText.toLowerCase()) !== -1,
                );
            }
        }

        setFilteredReportList(newFilteredReportList);
    }

    const handleSelecteReportChanged = async (item: Report) => {
        // if nothing changed, do nothing
        if (selectedReport && selectedReport.id === item.id) {
            return;
        }

        if (reportSelectionChangedTimeout) {
            clearTimeout(reportSelectionChangedTimeout);
        }

        reportSelectionChangedTimeout = setTimeout(() => {
            doReportSelectionChange(item);
        }, 200);

        async function doReportSelectionChange(item: Report) {
            props.progress(true);

            const reportDetails = await Backend.getReportDetails(props.config.rowid, accessToken, item.id, oktaAuth);

            const newSelectedItem = { ...reportDetails, name: item.name, id: item.id };
            if (!newSelectedItem.filters_json) {
                newSelectedItem.filters_json = '[]';
            }

            // simplify the filter list
            const filters = [];
            const filtersObject = JSON.parse(newSelectedItem.filters_json);
            for (const i in filtersObject) {
                if (filtersObject[i]) {
                    const fobj = filtersObject[i];
                    filters.push({ name: fobj.column, operator: fobj.operator, value: fobj.value });
                }
            }

            setSelectedReport(newSelectedItem);
            setSelectedReportFilters(filters);

            const { columns } = reportDetails;

            const newColumns = columns.map((item: ReportColumn, index: number) => ({
                label: item.label,
                api_name: item.api_name,
                data_type: item.data_type,
            }));

            setColumnList(newColumns);
            doColumnFilterChange(newColumns, columnFilterText);

            props.progress(false);
        }
    };

    function doColumnFilterChange(columnList: Array<ReportColumn>, filterText: string) {
        let newFilteredColumnList = [];

        newFilteredColumnList = columnList.filter((item) => {
            const verdict =
                !filterText ||
                filterText.trim().length === 0 ||
                item.label.toLowerCase().search(filterText.toLowerCase()) !== -1;

            return verdict;
        });

        setFilteredColumnList(newFilteredColumnList);
    }

    const handleColumnFilterChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (columnListFilterTimeout) {
            clearTimeout(columnListFilterTimeout);
        }

        const newColumnFilterText = e.target.value;

        columnListFilterTimeout = setTimeout(() => {
            setColumnFilterText(newColumnFilterText);
            doColumnFilterChange(columnList, newColumnFilterText);
        }, 300);
    };

    const handleSelectedColumnChanged = async (item: ReportColumn) => {
        // if nothing changed, do nothing
        if (selectedReportColumn && selectedReportColumn.api_name === item.api_name) {
            return;
        }

        if (columnSelectionChangedTimeout) {
            clearTimeout(columnSelectionChangedTimeout);
        }

        columnSelectionChangedTimeout = setTimeout(() => {
            doColumnSelectionChange(item);
        }, 200);

        async function doColumnSelectionChange(item: ReportColumn) {
            setSelectedReportColumn(item);
        }
    };

    const handleRunQuery = async () => {
        props.progress(true);
        const { resource_url } = props.config.environments[0];
        const responseData = await Backend.runSoqlQuery(
            props.config.rowid,
            accessToken,
            soqlQuery,
            oktaAuth,
            resource_url,
        );

        const data = JSON.parse(responseData.data.results);
        const totalRows = data.totalSize;
        const { records } = data;
        const newColumns = [];
        let newResults = [];

        if (totalRows > 0) {
            if (records.length === 0) {
                // this may be count query
                newColumns.push({ field: 'totalSize', headerName: t('sf-explorer.total-size'), width: 250 });
                newResults.push({ rowid: 0, totalSize: totalRows });
            } else {
                const flattenObj = (ob) => {
                    const result = {};
                    for (const i in ob) {
                        if (typeof ob[i] === 'object' && !Array.isArray(ob[i]) && ob[i] !== null) {
                            const temp = flattenObj(ob[i]);
                            for (const j in temp) {
                                if (temp[j]) {
                                    result[`${i}.${j}`] = temp[j];
                                }
                            }
                        } else {
                            result[i] = ob[i];
                        }
                    }
                    return result;
                };

                // get new columns definition
                const firstRow = flattenObj(records[0]);
                for (const p in firstRow) {
                    if (p.includes('attributes')) {
                        continue;
                    }

                    newColumns.push({ field: p, headerName: p, flex: 1, minWidth: 100 });
                }

                // setup fake row ids, otherwise datagrid will complain
                let i = 0;
                for (const r in records) {
                    if (records[r]) {
                        records[r].rowid = i++;
                        records[r] = flattenObj(records[r]);
                    }
                }
                newResults = records;
            }
        } else if (totalRows === undefined) {
            // query error
            const errorMessage = data[0].message;
            newColumns.push({ field: 'errorMessage', headerName: t('sf-explorer.error-message'), width: 500 });
            newResults.push({ rowid: 0, errorMessage });
        }

        setConsoleColumns(newColumns);
        setConsoleResults(newResults);
        setSelectedConsoleCellValue('');

        props.progress(false);
    };

    const [consoleColumns, setConsoleColumns] = useState<Array<any>>([]);
    const [consoleResults, setConsoleResults] = useState<Array<any>>([]);
    const [selectedConsoleCellValue, setSelectedConsoleCellValue] = useState<string>('');

    const consoleResultsCellSelected = (
        params: GridCellParams,
        event: MuiEvent<React.MouseEvent>,
        details: GridCallbackDetails,
    ) => {
        setSelectedConsoleCellValue(params.row);
    };

    return (
        <div className="SFExplorer">
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between">
                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        <Tab label={t('sf-explorer.objects')} id="0" />
                        <Tab label={t('sf-explorer.reports')} id="1" />
                        <Tab label={t('sf-explorer.console')} id="2" />
                    </Tabs>
                    {tabIndex === 2 && (
                        <Button
                            variant="contained"
                            onClick={handleRunQuery}
                            style={{ height: 36 }}
                            disabled={
                                !soqlQuery ||
                                soqlQuery.trim().length === 0 ||
                                !soqlQuery.trim().toLowerCase().startsWith('select')
                            }
                        >
                            {t('sf-explorer.run-query')}
                        </Button>
                    )}
                </Stack>
            </Box>
            <div role="tabpanel" hidden={tabIndex !== 0} style={{ height: '100%' }}>
                <Grid container spacing={2} className="TabContainer">
                    <Grid item xs={2} className="TabContainerItem">
                        <Stack direction="column" spacing={2} style={{ height: '100%' }}>
                            <TextField
                                placeholder={t('sf-explorer.search-objects')}
                                size="small"
                                onChange={handleObjectFilterChanged}
                            />
                            {filteredObjectList && filteredObjectList.length > 0 ? (
                                <List style={{ height: '100%', overflow: 'auto' }}>
                                    {filteredObjectList.map((item, index) => (
                                        <ListItemButton
                                            key={index}
                                            divider
                                            onClick={() => {
                                                handleSelectedObjectChanged(item);
                                            }}
                                        >
                                            <Stack direction="column">
                                                <ListItemText primary={item.label} />
                                                <Typography className="SelectedObjectApiName" variant="h6">
                                                    {item.api_name}
                                                </Typography>
                                            </Stack>
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : null}
                            {(!filteredObjectList || filteredObjectList.length === 0) && (
                                <div className="ObjectsSearchResultsHint">
                                    {t('sf-explorer.search-objects-results')}
                                </div>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item xs={4} className="TabContainerItem">
                        <Stack direction="column" spacing={2} style={{ height: '100%' }}>
                            <Grid container>
                                <Grid item xs={8}>
                                    <TextField
                                        className="FieldSearchInput"
                                        placeholder={t('sf-explorer.search-fields')}
                                        size="small"
                                        onChange={handleFieldFilterChanged}
                                    />
                                </Grid>
                                <Grid item xs={4} style={{ width: '100%' }}>
                                    <Select
                                        className="DataTypesList"
                                        size="small"
                                        value={selectedDataType}
                                        onChange={handleDataTypeChange}
                                    >
                                        {allDataTypes.map((item, index) => (
                                            <MenuItem key={index} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </Grid>
                            {filteredFieldList && filteredFieldList.length > 0 ? (
                                <List style={{ height: '100%', overflow: 'auto' }}>
                                    {filteredFieldList.map((item, index) => (
                                        <ListItemButton
                                            key={index}
                                            divider
                                            onClick={() => {
                                                handleSelectedFieldChanged(item);
                                            }}
                                        >
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : null}
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction="column" spacing={2}>
                            <Typography>{t('sf-explorer.object-details')}</Typography>
                            <Divider orientation="horizontal" />
                            {selectedObject ? (
                                <Grid container>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.object-name-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObject.label}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.object-api-name-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObject.api_name}
                                    </Grid>
                                </Grid>
                            ) : null}
                            <Typography>{t('sf-explorer.field-details')}</Typography>
                            <Divider orientation="horizontal" />
                            {selectedObjectField ? (
                                <Grid container>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObjectField.label}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-api-name')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Stack direction="row">
                                            {selectedObjectField.api_name}
                                            <CopyToClipboard text={selectedObjectField.api_name}>
                                                <IconButton style={{ width: '12px', height: '12px' }}>
                                                    <CopyToClipboardIcon style={{ width: '12px', height: '12px' }} />
                                                </IconButton>
                                            </CopyToClipboard>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-type')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObjectField.type}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-rel')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObjectField.relationshipName}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-ref')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedObjectField.referenceTo}
                                    </Grid>
                                    {selectedObjectField.api_name === 'RecordTypeId' && (
                                        <Grid item xs={9}>
                                            <Stack direction="column" spacing={2}>
                                                <Typography>
                                                    {t('sf-explorer.record-type')}
                                                    {':'}
                                                </Typography>
                                                <ul>
                                                    {recordTypeList.map(
                                                        (value: sObjectRecordTypeInfo, index: number) => (
                                                            <li key={index}>{value.developerName}</li>
                                                        ),
                                                    )}
                                                </ul>
                                            </Stack>
                                        </Grid>
                                    )}
                                    {selectedObjectField.picklistValues &&
                                    selectedObjectField.picklistValues.length > 0 ? (
                                        <Grid item xs={9}>
                                            <Stack direction="column" spacing={2}>
                                                <Typography>
                                                    {t('sf-explorer.possible-values')}
                                                    {':'}
                                                </Typography>
                                                <ul>
                                                    {selectedObjectField.picklistValues.map(
                                                        (value: sObjectFieldPicklistValue, index: number) => (
                                                            <li key={index}>{value.value}</li>
                                                        ),
                                                    )}
                                                </ul>
                                            </Stack>
                                        </Grid>
                                    ) : null}
                                </Grid>
                            ) : null}
                        </Stack>
                    </Grid>
                </Grid>
            </div>
            <div role="tabpanel" hidden={tabIndex !== 1} style={{ height: '100%' }}>
                <Grid container spacing={2} className="TabContainer">
                    <Grid item xs={2} className="TabContainerItem">
                        <Stack direction="column" spacing={2} style={{ height: '100%' }}>
                            <TextField
                                placeholder={t('sf-explorer.search-reports')}
                                size="small"
                                onChange={handleReportFilterChanged}
                            />
                            <div className="ReportSearchOnServerRoot">
                                <FormControlLabel
                                    className="ReportSearchOnServerLabel"
                                    control={
                                        <Switch
                                            size="small"
                                            checked={serverSearch}
                                            onChange={() => doServerSearch(!serverSearch)}
                                        />
                                    }
                                    label="Search on server"
                                />
                            </div>
                            {filteredReportList && filteredReportList.length > 0 ? (
                                <List style={{ height: '100%', overflow: 'auto' }}>
                                    {filteredReportList.map((item, index) => (
                                        <ListItemButton
                                            key={index}
                                            divider
                                            onClick={() => {
                                                handleSelecteReportChanged(item);
                                            }}
                                        >
                                            <ListItemText primary={item.name} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : null}
                            {(!filteredReportList || filteredReportList.length === 0) && (
                                <div className="ReportsSearchResultsHint">
                                    {t('sf-explorer.search-reports-results')}
                                </div>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item xs={4} className="TabContainerItem">
                        <Stack direction="column" spacing={2} style={{ height: '50%' }}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <TextField
                                        className="ColumnSearchInput"
                                        placeholder={t('sf-explorer.search-columns')}
                                        size="small"
                                        onChange={handleColumnFilterChanged}
                                    />
                                </Grid>
                            </Grid>
                            {filteredColumnList && filteredColumnList.length > 0 ? (
                                <List style={{ height: '100%', overflow: 'auto' }}>
                                    {filteredColumnList.map((item, index) => (
                                        <ListItemButton
                                            key={index}
                                            divider
                                            onClick={() => {
                                                handleSelectedColumnChanged(item);
                                            }}
                                        >
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : null}
                        </Stack>
                        {selectedReport ? (
                            <Stack direction="column" spacing={2} style={{ height: '50%' }}>
                                <Divider orientation="horizontal" />
                                <Typography variant="h6" className="Filter">
                                    {t('sf-explorer.filters')}
                                </Typography>
                                <TreeView
                                    defaultCollapseIcon={<ExpandMoreIcon />}
                                    defaultExpandIcon={<ChevronRightIcon />}
                                    sx={{ height: 240, flexGrow: 1, maxWidth: 450, overflowY: 'auto' }}
                                >
                                    {selectedReportFilters.map((f, i) => (
                                        <TreeItem key={i} nodeId={`${i}`} label={f.name}>
                                            <TreeItem
                                                nodeId={`${(i + 1) * 100}`}
                                                label={`${t('operator')} - ${f.operator}`}
                                            />
                                            <TreeItem
                                                nodeId={`${(i + 1) * 200}`}
                                                label={`${t('value')} - ${f.value}`}
                                            />
                                        </TreeItem>
                                    ))}
                                </TreeView>
                            </Stack>
                        ) : null}
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction="column" spacing={2}>
                            <Typography>{t('sf-explorer.report-details')}</Typography>
                            <Divider orientation="horizontal" />
                            {selectedReport ? (
                                <Grid container>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.report-name-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedReport.name}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.report-template-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedReport.report_type}
                                    </Grid>
                                </Grid>
                            ) : null}

                            <Typography>{t('sf-explorer.column-details')}</Typography>
                            <Divider orientation="horizontal" />
                            {selectedReportColumn ? (
                                <Grid container>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-label')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedReportColumn.label}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-api-name')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedReportColumn.api_name}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {t('sf-explorer.field-details-type')}
                                        {':'}
                                    </Grid>
                                    <Grid item xs={9}>
                                        {selectedReportColumn.data_type}
                                    </Grid>
                                </Grid>
                            ) : null}
                        </Stack>
                    </Grid>
                </Grid>
            </div>
            <div role="tabpanel" hidden={tabIndex !== 2} style={{ height: '100%' }}>
                <Grid container rowGap={2} style={{ paddingTop: 5 }}>
                    <Grid item xs={12}>
                        <TextField
                            value={soqlQuery}
                            onChange={(e) => {
                                setSoqlQuery(e.target.value);
                            }}
                            multiline
                            minRows={4}
                            maxRows={7}
                            fullWidth
                            placeholder={t('sf-explorer.enter-query')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <div style={{ height: 400, flexGrow: 1, maxWidth: 'calc(100vw - 220px)' }}>
                            <DataGridPro
                                components={{
                                    Toolbar:
                                        consoleResults.length > 0
                                            ? () =>
                                                  CustomGridToolbar(
                                                      props.config.config_name,
                                                      consoleResults.length > 1,
                                                      consoleResults,
                                                  )
                                            : null,
                                }}
                                rows={consoleResults}
                                columns={consoleColumns}
                                pagination
                                pageSizeOptions={[100, 200]}
                                initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
                                getRowId={(row) => row.rowid}
                                rowHeight={26}
                                onCellClick={consoleResultsCellSelected}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            value={selectedConsoleCellValue}
                            multiline
                            minRows={3}
                            maxRows={3}
                            fullWidth
                            placeholder={t('sf-explorer.select-value')}
                        />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default SFExplorer;
